import { Component, OnInit, ViewChild } from '@angular/core';
import { TcErrorType } from '@tc-core/model/it/codegen/tbx/ext/errors/tc-error';
import { DialogModel, LEVEL } from '@tc-core/model/it/codegen/ui/framework/dialog-model';
import { ConfigLoader } from '@tc-core/util/framework/config-loader.service';
import { SpinnerService } from '@tc-core/util/ui/spinner.service';
import { DialogService } from '@tc/dialog/dialog.service';
import { IGetRowsParams } from 'ag-grid-community';
import { of, Subscription } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import { tap } from 'rxjs/operators';
import { UserProfile } from '../../../authentication/user-profile';
import { TCO } from '../../../constants';
import { DivisionSearchCriteria } from '../../../models/criteria/division-search-criteria';
import { SortDirection } from '../../../models/helper/sort-direction';
import {
    MasterDataRestService
} from '../../../services/backend-consumers/master-data-handler-service/master-data-rest.service';
import { CostingTypeService } from '../../../services/backend-consumers/setups/costing-type-service';
import { DivisionSetupService } from '../../../services/backend-consumers/setups/division-setup-service';
import { UserManagementService } from '../../../services/user-management/user-management.service';
import { DMCCommon } from '../../../services/util/common/dmc-common';
import { DataKey, DataStoreService } from '../../../services/util/framework/data-store.service';
import {
    SetupCommonDataProcessorService
} from '../../../services/util/pre-processors/setup-common-data-processor.service';
import {
    SetupGridComp
} from '../../../widgets/framework/ag-grid-custom-components/components/setup-grid/setup-grid-comp';
import {
    SetupGridDataSource
} from '../../../widgets/framework/ag-grid-custom-components/components/setup-grid/setup-grid-data-source';
import {
    SetupGridComponent
} from '../../../widgets/framework/ag-grid-custom-components/components/setup-grid/setup-grid.component';

@Component({
    selector: 'tc-division-setup',
    templateUrl: './division-setup.component.html'
})
export class DivisionSetupComponent extends SetupGridComp implements OnInit, SetupGridDataSource {
    @ViewChild('setupGrid')
    private setupGrid: SetupGridComponent;
    public colDefConfig = [];
    private hasADivision = false;
    userProfile: UserProfile;
    private defCompany: string;

    // search criteria obj. used to
    private divisionSearchCriteria: DivisionSearchCriteria = new DivisionSearchCriteria();

    private searchCriteriaObserver: Subscription = new Subscription();
    private dialogSubscription: Subscription = new Subscription();

    constructor(
        private configLoader: ConfigLoader,
        private costingTypeService: CostingTypeService,
        private dataStoreService: DataStoreService,
        private masterDataRestService: MasterDataRestService,
        private common: DMCCommon,
        private dataStore: DataStoreService,
        private divisionSetupService: DivisionSetupService,
        private dialogService: DialogService,
        private userManagementService: UserManagementService,
        private spinnerService: SpinnerService,
        private setupCommonDataProcessorService: SetupCommonDataProcessorService
    ) {
        super();
    }

    ngOnInit() {
        this.divisionAvailability();
        this.colDefConfig = this.configLoader.configurations.get(TCO.CONF.CONF_DIVISION_SETUP_CONFIG);
        this.subscribeSearchCriteria();
    }

    /**
     * subscribe search criteria changes in search bar
     * then force grid to refresh data
     * grid refresh will be call getRaws method which is using this subscribed criteria for backend call
     */
    subscribeSearchCriteria() {
        this.searchCriteriaObserver = this.dataStoreService.get(DataKey.divisionSetupSearchCriteria)
                                          .subscribe(value => {
                                              this.divisionSearchCriteria = value;
                                              this.setupGrid.runForceSearch();
                                          });
    }

    createNewRow(): any {
        if (this.dataStore.get(DataKey.kcCompanies) && this.dataStore.get(DataKey.kcCompanies).getValue()
            && this.dataStore.get(DataKey.kcCompanies).getValue()[0] &&
            this.dataStore.get(DataKey.kcCompanies).getValue()[0].name) {
            this.defCompany = this.dataStore.get(DataKey.kcCompanies).getValue()[0].code;
            return {company: this.dataStore.get(DataKey.kcCompanies).getValue()[0].name};
        }
        return '-';
    }

    public divisionAvailability() {
        this.hasADivision = this.userProfile !== null && this.userProfile !== undefined &&
            this.userProfile.kcDivision !== null && this.userProfile.kcDivision !== undefined;
    }

    deleteRow(row: any): Observable<any> {
        return of('success');
    }

    public getRows(params: IGetRowsParams): Observable<any> {
        if (!this.divisionSearchCriteria) {
            this.divisionSearchCriteria = new DivisionSearchCriteria();
        }

        if (params && params.startRow) {
            this.divisionSearchCriteria.start = params.startRow;
        }
        this.divisionSearchCriteria = this.userManagementService.updateSearchCriteriaWithUserKC(this.divisionSearchCriteria);
        if (params && params.endRow && params.startRow) {
            this.divisionSearchCriteria.size = params.endRow - params.startRow;
        }
        if (params.sortModel && params.sortModel[0]) {
            this.divisionSearchCriteria.sortBy = params.sortModel[0].colId;
            if (params.sortModel[0].sort === SetupGridComp.GRID_SORT_ASCENDING) {
                this.divisionSearchCriteria.sortDirection = SortDirection.ASC;
            } else {
                this.divisionSearchCriteria.sortDirection = SortDirection.DESC;
            }
        }

        const userProfile = this.dataStore.get(DataKey.userProfile).getValue();
        if (userProfile.kcCompany != null) {
            this.divisionSearchCriteria.kcCompany = userProfile.kcCompany;
        }
        return this.divisionSetupService.getRows(this.divisionSearchCriteria)
                   .pipe(
                       tap(
                           data => {
                               if (data) {
                                   this.dataStoreService.set(DataKey.divisionSetupSearchResultsForCriteria, data);
                               } else {

                               }
                           }
                       )
                   );
    }

    public isInvalidRow(params): boolean {
        const data = params.data;
        if (data) {
            let invalid = true;
            if (
                data.code &&
                data.name &&
                data.company
            ) {
                invalid = false;
            }
            return invalid;
        } else {
            return false;
        }
    }

    public isUnsavedRow(row: any): boolean {
        if (row) {
            return !row.id;
        }
        return true;
    }

    saveRow(row: any, event: any): Observable<any> {
        if (this.isUnsavedRow(row)) {
            const confirmSendManifests = new DialogModel(
                true,
                LEVEL.WARNING,
                'Save Division',
                'The entered division cannot be modified or deleted later. Do you want to continue?',
                true,
                2000,
                null,
                'Cancel',
                'Save',
                true
            );
            confirmSendManifests.disableClose = true;
            if (this.dialogSubscription) {
                this.dialogSubscription.unsubscribe();
            }
            const dialogSubscription = this.dialogService
                                           .confirm(confirmSendManifests)
                                           .subscribe((res) => {
                                                   if (res === true) {
                                                       row.kcCompany = this.defCompany;
                                                       return this.divisionSetupService.saveRow(row)
                                                                  .subscribe(
                                                                      (response) => {
                                                                          this.userManagementService.getKcCompaniesAndDivisions();
                                                                          this.spinnerService.show();
                                                                          dialogSubscription.unsubscribe();
                                                                          this.setupGrid.runForceSearch();
                                                                          this.spinnerService.hide();
                                                                      },
                                                                      error => {
                                                                          this.spinnerService.hide();
                                                                          if (event && event.data) {
                                                                              event.data.name = '';
                                                                          }
                                                                          this.common.showSnackBar(
                                                                              'Division code cannot be duplicated',
                                                                              3000,
                                                                              TcErrorType.TYPE.ERROR
                                                                          );
                                                                      }
                                                                  );
                                                   } else {
                                                       if (event && event.data) {
                                                           event.data.name = '';
                                                       }
                                                   }
                                               }
                                           );
        }
        return null;
    }

    public isDisabledAdd() {
        const userProfile = this.dataStore.get(DataKey.userProfile).getValue();
        return (userProfile && userProfile.kcDivision);

    }

    public ngOnDestroy(): void {
        if (this.searchCriteriaObserver) {
            this.searchCriteriaObserver.unsubscribe();
        }
        if (this.dialogSubscription) {
            this.dialogSubscription.unsubscribe();
        }
    }
}
